import React, { useState, useEffect } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import PDFDocument from '../components/PrivateCatalogManager/PDFDesignModal/PDFTemplates/PDFDocument';
import QRCodeGenerator from '../components/PrivateCatalogManager/PDFDesignModal/PDFTemplates/QRCodeGenerator';
import IconToBase64 from '../components/PrivateCatalogManager/PDFDesignModal/PDFTemplates/IconToBase64';
import CircularLogo from '../components/PrivateCatalogManager/PDFDesignModal/PDFTemplates/CircularLogo';
import './PDFPreviewPage.css';

const PDFPreviewPage = () => {
    const [products, setProducts] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [providerInfo, setProviderInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfKey, setPdfKey] = useState(0);
    const [qrWhatsApp, setQrWhatsApp] = useState(null);
    const [qrCatalog, setQrCatalog] = useState(null);
    const [whatsappIcon, setWhatsappIcon] = useState(null);
    const [circularLogo, setCircularLogo] = useState(null);

    // Configuración de ejemplo (puedes cambiarla aquí directamente)
    const [pdfConfig, setPdfConfig] = useState({
        layout: 'doble-columna', // 'alternado', 'doble-columna', 'grid-3x3', 'masonry'
        colorPalette: 'orange',
        showPrices: true,
        showDescriptions: true,
        showCodes: false,
        showContactPage: true,
        coverType: 'generated' // 'generated' o 'banner'
    });

    const colors = {
        orange: { primary: '#fa7e17', secondary: '#ff9a3c', accent: '#e06d0f' },
        blue: { primary: '#2563eb', secondary: '#3b82f6', accent: '#1d4ed8' },
        green: { primary: '#10b981', secondary: '#34d399', accent: '#059669' },
        purple: { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#7c3aed' },
        red: { primary: '#ef4444', secondary: '#f87171', accent: '#dc2626' },
        teal: { primary: '#14b8a6', secondary: '#2dd4bf', accent: '#0d9488' },
        pink: { primary: '#ec4899', secondary: '#f472b6', accent: '#db2777' },
        gray: { primary: '#6b7280', secondary: '#9ca3af', accent: '#4b5563' }
    };

    useEffect(() => {
        // Convertir imagen a base64
        const convertImageToBase64 = async (imageUrl) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.src = imageUrl;
                
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        const maxWidth = 1200;
                        let width = img.width;
                        let height = img.height;
                        
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                        
                        canvas.width = width;
                        canvas.height = height;
                        
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);
                        
                        resolve(canvas.toDataURL('image/png', 0.8));
                    } catch (error) {
                        reject(error);
                    }
                };
                
                img.onerror = (error) => {
                    reject(error);
                };
            });
        };

        // Datos de ejemplo para desarrollo
        const mockProducts = [
            {
                id: 1,
                nombre: 'Camiseta',
                descripcion: 'Algodón premium.',
                codigo: 'CAM-001',
                categoria: 'Ropa',
                subcategoria: 'Camisetas',
                precios: { 
                    'Por Mayor': '$25.000',
                    'Detal': '$35.000'
                },
                variantes: [
                    { nombre: 'S' },
                    { nombre: 'M' },
                    { nombre: 'L' },
                    { nombre: 'XL' },
                    { nombre: 'Negro' },
                    { nombre: 'Blanco' },
                    { nombre: 'Gris' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=600']
            },
            {
                id: 2,
                nombre: 'Jean Clásico',
                descripcion: 'Mezclilla resistente.',
                codigo: 'PANT-002',
                categoria: 'Ropa',
                subcategoria: 'Pantalones',
                precios: { 
                    'Precio': '$65.000'
                },
                variantes: [
                    { nombre: '28' },
                    { nombre: '30' },
                    { nombre: '32' },
                    { nombre: '34' },
                    { nombre: 'Azul Oscuro' },
                    { nombre: 'Negro' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=500']
            },
            {
                id: 3,
                nombre: 'Chaqueta',
                descripcion: 'Ligera y transpirable.',
                codigo: 'CHAQ-003',
                categoria: 'Ropa',
                subcategoria: 'Chaquetas',
                precios: { 
                    'Precio': '$120.000'
                },
                variantes: [
                    { nombre: 'S' },
                    { nombre: 'M' },
                    { nombre: 'L' },
                    { nombre: 'XL' },
                    { nombre: 'Negro' },
                    { nombre: 'Azul' },
                    { nombre: 'Rojo' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=550']
            },
            {
                id: 4,
                nombre: 'Zapatos',
                descripcion: 'Cómodos y ergonómicos.',
                codigo: 'ZAP-004',
                categoria: 'Calzado',
                subcategoria: 'Casuales',
                precios: { 
                    'Precio': '$90.000'
                },
                variantes: [
                    { nombre: '38' },
                    { nombre: '39' },
                    { nombre: '40' },
                    { nombre: '41' },
                    { nombre: '42' },
                    { nombre: 'Negro' },
                    { nombre: 'Café' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=400']
            },
            {
                id: 5,
                nombre: 'Gorra UV',
                descripcion: 'Protección solar.',
                codigo: 'GOR-005',
                categoria: 'Accesorios',
                subcategoria: 'Gorras',
                precios: { 
                    'Por Mayor': '$15.000',
                    'Detal': '$25.000'
                },
                variantes: [
                    { nombre: 'Negro' },
                    { nombre: 'Blanco' },
                    { nombre: 'Azul' },
                    { nombre: 'Rojo' },
                    { nombre: 'Verde' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&h=450']
            },
            {
                id: 6,
                nombre: 'Mochila',
                descripcion: 'Espaciosa y práctica.',
                codigo: 'MOCH-006',
                categoria: 'Accesorios',
                subcategoria: 'Mochilas',
                precios: { 
                    'Precio': '$80.000'
                },
                variantes: [
                    { nombre: 'Negro' },
                    { nombre: 'Gris' },
                    { nombre: 'Azul Marino' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=600']
            },
            {
                id: 7,
                nombre: 'Sudadera',
                descripcion: 'Cómoda con capucha.',
                codigo: 'SUD-007',
                categoria: 'Ropa',
                subcategoria: 'Sudaderas',
                precios: { 
                    'Precio': '$55.000'
                },
                variantes: [
                    { nombre: 'S' },
                    { nombre: 'M' },
                    { nombre: 'L' },
                    { nombre: 'XL' },
                    { nombre: 'Negro' },
                    { nombre: 'Gris' },
                    { nombre: 'Azul' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500']
            },
            {
                id: 8,
                nombre: 'Shorts',
                descripcion: 'Secado rápido.',
                codigo: 'SHORT-008',
                categoria: 'Ropa',
                subcategoria: 'Shorts',
                precios: { 
                    'Por Mayor': '$20.000',
                    'Detal': '$30.000'
                },
                variantes: [
                    { nombre: 'M' },
                    { nombre: 'L' },
                    { nombre: 'XL' },
                    { nombre: 'Negro' },
                    { nombre: 'Azul' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1591195853828-11db59a44f6b?w=400&h=550']
            },
            {
                id: 9,
                nombre: 'Reloj',
                descripcion: 'Resistente al agua.',
                codigo: 'REL-009',
                categoria: 'Accesorios',
                subcategoria: 'Relojes',
                precios: { 
                    'Precio': '$45.000'
                },
                variantes: [
                    { nombre: 'Negro' },
                    { nombre: 'Azul' },
                    { nombre: 'Rojo' },
                    { nombre: 'Blanco' }
                ],
                imagenes: ['https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400']
            }
        ];

        const mockCatalog = {
            id: 1,
            name: 'Catálogo de Productos 2026',
            description: 'Colección exclusiva de ropa y accesorios',
            bannerUrl: 'https://cdn.minymol.com/catalog-banners/080495e7-38f5-4396-94b4-62e17673129b.webp'
        };

        const mockProviderInfo = {
            nombre_empresa: 'Distribuidora MinyMol S.A.S.',
            telefono: '+57 300 123 4567',
            email: 'ventas@minymol.com',
            direccion: 'Calle 123 #45-67, Bogotá',
            ciudad: 'Bogotá',
            departamento: 'Cundinamarca',
            logo_url: 'https://cdn.minymol.com/provider-media/logos/33-8f15da52-fdb2-4cfb-a3aa-ae630809595d.webp'
        };

        // Convertir todas las imágenes a base64
        const loadProductsWithBase64Images = async () => {
            try {
                const productsWithBase64 = await Promise.all(
                    mockProducts.map(async (product) => {
                        if (product.imagenes && product.imagenes.length > 0) {
                            const base64Images = await Promise.all(
                                product.imagenes.map(img => convertImageToBase64(img))
                            );
                            return {
                                ...product,
                                imagenes: base64Images
                            };
                        }
                        return product;
                    })
                );
                
                // Convertir el banner del catálogo a base64
                let catalogWithBase64Banner = mockCatalog;
                if (mockCatalog.bannerUrl) {
                    try {
                        const base64Banner = await convertImageToBase64(mockCatalog.bannerUrl);
                        catalogWithBase64Banner = {
                            ...mockCatalog,
                            bannerUrl: base64Banner
                        };
                    } catch (error) {
                        console.error('Error convirtiendo banner:', error);
                    }
                }
                
                setProducts(productsWithBase64);
                setCatalog(catalogWithBase64Banner);
                setProviderInfo(mockProviderInfo);
                setLoading(false);
            } catch (error) {
                console.error('Error convirtiendo imágenes:', error);
                setProducts(mockProducts);
                setCatalog(mockCatalog);
                setProviderInfo(mockProviderInfo);
                setLoading(false);
            }
        };

        loadProductsWithBase64Images();
    }, []);

    if (loading) {
        return (
            <div className="pdf-preview-page-loading">
                <div className="spinner"></div>
                <p>Cargando vista previa...</p>
            </div>
        );
    }

    return (
        <div className="pdf-preview-page">
            {/* Generadores de QR (ocultos, solo para generar las imágenes) */}
            {providerInfo && (
                <>
                    {/* Generar icono de WhatsApp */}
                    <IconToBase64 
                        size={80}
                        onGenerated={(iconBase64) => setWhatsappIcon(iconBase64)}
                    />
                    
                    {/* Generar logo circular del proveedor */}
                    <CircularLogo
                        logoUrl={providerInfo.logo_url}
                        size={80}
                        onGenerated={(logoBase64) => setCircularLogo(logoBase64)}
                    />
                    
                    {/* Generar QR de WhatsApp (esperar a que el icono esté listo) */}
                    {whatsappIcon && (
                        <QRCodeGenerator
                            value={`https://wa.me/${providerInfo.telefono?.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(providerInfo.nombre_empresa || '')},%20estoy%20interesado%20en%20sus%20productos`}
                            logo={whatsappIcon}
                            size={300}
                            fgColor="#25D366"
                            onGenerated={(qrImage) => setQrWhatsApp(qrImage)}
                        />
                    )}
                    
                    {/* Generar QR del catálogo (esperar a que el logo circular esté listo) */}
                    {circularLogo && (
                        <QRCodeGenerator
                            value="https://share.minymol.com/share/SnwWABk8Rkbu"
                            logo={circularLogo}
                            size={300}
                            fgColor="#000000"
                            onGenerated={(qrImage) => setQrCatalog(qrImage)}
                        />
                    )}
                </>
            )}
            
            <div className="pdf-preview-sidebar">
                <h2>🎨 Configuración del PDF</h2>
                <p className="pdf-preview-hint">
                    Cambia estos valores y guarda para ver los cambios en tiempo real
                </p>

                <div className="pdf-preview-control">
                    <label>Layout:</label>
                    <select 
                        value={pdfConfig.layout} 
                        onChange={(e) => setPdfConfig({...pdfConfig, layout: e.target.value})}
                    >
                        <option value="alternado">Alternado</option>
                        <option value="doble-columna">Doble Columna</option>
                        <option value="grid-3x3">Grid 3x3</option>
                        <option value="masonry">Masonry</option>
                    </select>
                </div>

                <div className="pdf-preview-control">
                    <label>Color:</label>
                    <select 
                        value={pdfConfig.colorPalette} 
                        onChange={(e) => setPdfConfig({...pdfConfig, colorPalette: e.target.value})}
                    >
                        <option value="orange">Naranja</option>
                        <option value="blue">Azul</option>
                        <option value="green">Verde</option>
                        <option value="purple">Morado</option>
                        <option value="red">Rojo</option>
                        <option value="teal">Teal</option>
                        <option value="pink">Rosa</option>
                        <option value="gray">Gris</option>
                    </select>
                </div>

                <div className="pdf-preview-control">
                    <label>Tipo de Portada:</label>
                    <select 
                        value={pdfConfig.coverType} 
                        onChange={(e) => setPdfConfig({...pdfConfig, coverType: e.target.value})}
                    >
                        <option value="generated">Generada</option>
                        <option value="banner">Banner</option>
                    </select>
                </div>

                <div className="pdf-preview-control">
                    <label>
                        <input 
                            type="checkbox" 
                            checked={pdfConfig.showPrices}
                            onChange={(e) => setPdfConfig({...pdfConfig, showPrices: e.target.checked})}
                        />
                        Mostrar precios
                    </label>
                </div>

                <div className="pdf-preview-control">
                    <label>
                        <input 
                            type="checkbox" 
                            checked={pdfConfig.showDescriptions}
                            onChange={(e) => setPdfConfig({...pdfConfig, showDescriptions: e.target.checked})}
                        />
                        Mostrar descripciones
                    </label>
                </div>

                <div className="pdf-preview-control">
                    <label>
                        <input 
                            type="checkbox" 
                            checked={pdfConfig.showCodes}
                            onChange={(e) => setPdfConfig({...pdfConfig, showCodes: e.target.checked})}
                        />
                        Mostrar códigos
                    </label>
                </div>

                <div className="pdf-preview-control">
                    <label>
                        <input 
                            type="checkbox" 
                            checked={pdfConfig.showContactPage}
                            onChange={(e) => setPdfConfig({...pdfConfig, showContactPage: e.target.checked})}
                        />
                        Página de contacto
                    </label>
                </div>

                <div className="pdf-preview-info">
                    <h3>💡 Tips:</h3>
                    <ul>
                        <li>Edita <code>PDFDocument.js</code></li>
                        <li>Guarda el archivo (Ctrl+S)</li>
                        <li>El PDF se actualiza automáticamente</li>
                        <li>Usa los controles de arriba para probar diferentes configuraciones</li>
                    </ul>
                </div>

                <button 
                    className="pdf-preview-reload-btn"
                    onClick={() => setPdfKey(prev => prev + 1)}
                    title="Recargar PDF"
                >
                    🔄 Recargar PDF
                </button>
            </div>

            <div className="pdf-preview-viewer-container">
                <PDFViewer 
                    key={pdfKey}
                    style={{ 
                        width: '100%', 
                        height: '100%',
                        border: 'none'
                    }}
                    showToolbar={true}
                >
                    <PDFDocument
                        catalog={catalog}
                        products={products}
                        config={pdfConfig}
                        colors={colors[pdfConfig.colorPalette]}
                        providerInfo={providerInfo}
                        qrWhatsApp={qrWhatsApp}
                        qrCatalog={qrCatalog}
                        circularLogo={circularLogo}
                    />
                </PDFViewer>
            </div>
        </div>
    );
};

export default PDFPreviewPage;
